/**
 * Single source of truth for all supported locales.
 *
 * - `code`             Short code used by the Nuxt i18n module, URL params and API routes.
 * - `language`         BCP 47 language tag used by the browser and Nuxt i18n internally.
 * - `countryOverrides` Country-specific BCP 47 tags that differ from the default `language`.
 *                      Keyed by lowercase country code (e.g. `at` → `de-AT`).
 *                      Used for hreflang attributes, sitemap links and search filtering.
 */
export const LOCALES = [
    { code: 'de', language: 'de-DE', countryOverrides: { at: 'de-AT' } },
    { code: 'en', language: 'en-GB', countryOverrides: {} },
    { code: 'sv', language: 'sv-SE', countryOverrides: {} },
    { code: 'da', language: 'da-DK', countryOverrides: {} },
    { code: 'nb', language: 'nb-NO', countryOverrides: {} },
    { code: 'fr', language: 'fr-FR', countryOverrides: {} },
    { code: 'es', language: 'es-ES', countryOverrides: {} }
] as const;

export type LocaleCode = (typeof LOCALES)[number]['code'];

export const DEFAULT_LOCALE: LocaleCode = 'de';

export function isLocaleCode(value: string): value is LocaleCode {
    return LOCALES.some((locale) => locale.code === value);
}

export function mapLocales<T>(create: (code: LocaleCode) => T): Record<LocaleCode, T> {
    return Object.fromEntries(LOCALES.map(({ code }) => [code, create(code)])) as Record<LocaleCode, T>;
}
